'use client';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

// Types matching backend response
export interface TrendScore {
  id: number;
  overall_score: number;
  velocity_score: number;
  volume_score: number;
  monetization_score: number;
  cross_platform_score: number;
  is_accelerating: boolean;
  velocity_indicator: string;
  categories: string[] | null;
  reasoning_why_growing: string | null;
  reasoning_monetization: string | null;
  reasoning_risks: string | null;
  scoring_stage: string;
  created_at: string;
}

export interface ScoredTrend {
  id: number;
  title: string;
  source: string;
  language: string;
  normalized_volume: number;
  raw_metrics: Record<string, unknown> | null;
  source_url: string | null;
  category: string | null;
  external_id: string | null;
  created_at: string;
  updated_at: string;
  score: TrendScore | null;
  correlation_count: number;
  monetization_preview: string | null;
  recent_volumes: number[];
}

export interface TrendFilters {
  search?: string;
  source?: string;
  min_score?: number;
  max_score?: number;
  language?: string;
  category?: string;
  date_from?: string;
  date_to?: string;
  sort_by?: string;
  order?: string;
}

export interface OverviewStats {
  total_trends: number;
  average_score: number;
  active_sources: { active: number; total: number };
  accelerating_count: number;
}

function buildFilterParams(filters: TrendFilters): string {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.source) params.set('source', filters.source);
  if (filters.min_score != null) params.set('min_score', String(filters.min_score));
  if (filters.max_score != null) params.set('max_score', String(filters.max_score));
  if (filters.language) params.set('language', filters.language);
  if (filters.category) params.set('category', filters.category);
  if (filters.date_from) params.set('date_from', filters.date_from);
  if (filters.date_to) params.set('date_to', filters.date_to);
  if (filters.sort_by) params.set('sort_by', filters.sort_by);
  if (filters.order) params.set('order', filters.order);
  return params.toString();
}

const PAGE_SIZE = 50;

export function useScoredTrends(filters: TrendFilters) {
  return useInfiniteQuery<ScoredTrend[]>({
    queryKey: ['scored-trends', filters],
    queryFn: async ({ pageParam }) => {
      const filterStr = buildFilterParams(filters);
      const sep = filterStr ? '&' : '';
      return apiFetch<ScoredTrend[]>(
        `/scoring/trends?${filterStr}${sep}limit=${PAGE_SIZE}&offset=${pageParam}`
      );
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      return allPages.flat().length;
    },
    initialPageParam: 0,
  });
}

export function useOverviewStats() {
  return useQuery<OverviewStats>({
    queryKey: ['overview-stats'],
    queryFn: () => apiFetch<OverviewStats>('/stats/overview'),
    staleTime: 60_000,
  });
}
